import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import {
  IoArrowBack,
  IoAddCircle,
  IoClose,
  IoCall,
  IoNavigate,
  IoLocation,
} from "react-icons/io5";

import {
  getCustomUser,
  updateUser,
  getUstas,
  createUsta,
} from "../api";

import { getStableDeviceId } from "../utils/deviceId";
import "../styles/sifaris.css";
import "leaflet/dist/leaflet.css";

const INITIAL_CENTER = [40.4093, 49.8671];

const selectedIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: "sq-master-marker-selected",
});

const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function MapLongPressPicker({ isAdding, onPick }) {
  useMapEvents({
    contextmenu(e) {
      if (!isAdding) return;
      onPick({
        latitude: e.latlng.lat,
        longitude: e.latlng.lng,
      });
    },
  });

  return null;
}

const MasterScreen = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const listRefs = useRef({});

  const [items, setItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [name, setName] = useState("");
  const [hours, setHours] = useState("");
  const [phone, setPhone] = useState("");
  const [picked, setPicked] = useState(null);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });

  const showModal = ({ title, message, type = "info" }) => {
    setModal({ open: true, title, message, type });
  };

  const resetForm = () => {
    setName("");
    setHours("");
    setPhone("");
    setPicked(null);
  };

  const canSave = useMemo(
    () => !!name.trim() && !!hours.trim() && !!phone.trim() && !!picked,
    [name, hours, phone, picked]
  );

  const toItemShape = (u) => {
    let lat = 0;
    let lng = 0;

    if (Array.isArray(u.location) && u.location.length >= 2) {
      const a = String(u.location[0]).trim();
      const b = String(u.location[1]).trim();

      if (a.includes(",") && !b) {
        const [la, lo] = a.split(",").map(Number);
        lat = la;
        lng = lo;
      } else {
        lat = Number(a);
        lng = Number(b);
      }
    }

    return {
      id: String(u.id),
      name: u.name,
      phone: u.phone,
      hours: u.hours,
      createdAt: u.createdAt,
      isDisable: !!u.isDisable,
      location: {
        latitude: lat,
        longitude: lng,
      },
    };
  };

  const load = useCallback(async () => {
    try {
      const res = await getUstas();
      const arr = Array.isArray(res?.data) ? res.data : [];

      const mapped = arr
        .filter((x) => !x.isDisable)
        .map(toItemShape)
        .sort((a, b) => {
          const ta = new Date(a.createdAt || 0).getTime();
          const tb = new Date(b.createdAt || 0).getTime();
          return tb - ta;
        });

      setItems(mapped);
    } catch (e) {
      console.log("getUstas error", e?.message);
      showModal({
        title: "Xəta",
        message: "Ustalar yüklənmədi.",
        type: "error",
      });
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const storedUserId = localStorage.getItem("id");
        const storedPassword = localStorage.getItem("password");
        const stableDeviceId = await getStableDeviceId();

        localStorage.setItem("deviceUUID", stableDeviceId);

        if (storedUserId) {
          const response = await getCustomUser(storedUserId);

          if (response?.data) {
            if (response.data.isDisable) {
              const hasExpiry =
                response.data.disableExpiryDate &&
                String(response.data.disableExpiryDate).trim().length > 0;

              if (hasExpiry) {
                const disableExpiryDate = moment(
                  response.data.disableExpiryDate,
                  "YYYY-MM-DD HH:mm:ss",
                  true
                );
                const currentTime = moment();

                if (disableExpiryDate.isValid() && disableExpiryDate.isBefore(currentTime)) {
                  await updateUser(storedUserId, {
                    isDisable: false,
                    disableExpiryDate: "",
                  });

                  showModal({
                    title: "Diqqət",
                    message:
                      "Hesabınızın bloklanma müddəti bitib. Hesabınız yenidən aktivləşdirildi.",
                    type: "success",
                  });
                } else {
                  localStorage.clear();
                  navigate("/sifaris/login", { replace: true });
                  return;
                }
              } else {
                localStorage.clear();
                navigate("/sifaris/login", { replace: true });
                return;
              }
            }

            if (storedPassword && response.data.password !== storedPassword) {
              localStorage.clear();
              navigate("/sifaris/login", { replace: true });
              return;
            }

            const storedDeviceId = localStorage.getItem("deviceUUID");
            if (
              storedDeviceId &&
              response.data.phoneId &&
              storedDeviceId !== response.data.phoneId
            ) {
              localStorage.clear();
              navigate("/sifaris/login", { replace: true });
              return;
            }
          }
        }

        await load();
      } catch (error) {
        console.error("MasterScreen init error:", error);
      }
    };

    initializeApp();
  }, [load, navigate]);

  const centerOn = (coord) => {
    if (!mapRef.current) return;
    mapRef.current.setView([coord.latitude, coord.longitude], 16, {
      animate: true,
    });
  };

  const onPickMap = (coord) => {
    setPicked(coord);
    centerOn(coord);
  };

  const addPlace = async () => {
    if (!canSave) {
      showModal({
        title: "Diqqət",
        message: "Xəritədə yer seçin və bütün xanaları doldurun.",
        type: "warning",
      });
      return;
    }

    try {
      const nowBaku = moment().tz("Asia/Baku").format("YYYY-MM-DD HH:mm:ss");

      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        hours: hours.trim(),
        location: [
          String(Number(picked.latitude).toFixed(6)),
          String(Number(picked.longitude).toFixed(6)),
        ],
        isDisable: false,
        createdAt: nowBaku,
      };

      await createUsta(payload);
      resetForm();
      setIsAdding(false);
      await load();
    } catch (e) {
      console.log("createUsta error", e?.response?.data || e?.message);
      showModal({
        title: "Xəta",
        message: "Usta əlavə edilə bilmədi.",
        type: "error",
      });
    }
  };

  const callNow = (num) => {
    window.location.href = `tel:${num}`;
  };

  const openDirections = (coord) => {
    const { latitude, longitude } = coord;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
    window.open(url, "_blank");
  };

  const handleSelectItem = (item) => {
    setSelectedId(item.id);
    centerOn(item.location);

    const node = listRefs.current[item.id];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="sq-master-page">
        
      <div className="sq-master-header">
        <div className="sq-master-header-left">
          <button
            type="button"
            className="sq-master-back-btn"
            onClick={() => navigate(-1)}
          >
            <IoArrowBack size={22} />
          </button>

          <h1 className="sq-master-title">Ustalar</h1>
        </div>

        <button
          type="button"
          className="sq-master-add-toggle-btn"
          onClick={() => {
            if (isAdding) {
              resetForm();
              setIsAdding(false);
            } else {
              setIsAdding(true);
              setSelectedId(null);
            }
          }}
        >
          {isAdding ? <IoClose size={18} /> : <IoAddCircle size={18} />}
          <span>{isAdding ? "İmtina" : "Usta əlavə et"}</span>
        </button>
      </div>

      <div className="sq-master-body">
        <div className="sq-master-map-wrap">
          <MapContainer
            center={INITIAL_CENTER}
            zoom={13}
            scrollWheelZoom
            className="sq-master-map"
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapLongPressPicker isAdding={isAdding} onPick={onPickMap} />

            {isAdding && picked && (
              <Marker
                position={[picked.latitude, picked.longitude]}
                icon={selectedIcon}
              >
                <Popup>Seçilən yer</Popup>
              </Marker>
            )}

            {items.map((it) => (
              <Marker
                key={it.id}
                position={[it.location.latitude, it.location.longitude]}
                icon={selectedId === it.id ? selectedIcon : defaultIcon}
                eventHandlers={{
                  click: () => handleSelectItem(it),
                }}
              >
                <Popup>
                  <strong>{it.name}</strong>
                  <br />
                  {it.hours}
                  <br />
                  {it.phone}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="sq-master-bottom-wrap">
          {isAdding ? (
            <div className="sq-master-form">
              <input
                className="sq-master-input"
                placeholder="Yerin adı (məs: Usta Ramin)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <input
                className="sq-master-input"
                placeholder="İş saatı (məs: 09:00 — 21:00)"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />

              <input
                className="sq-master-input"
                placeholder="Əlaqə nömrəsi (məs: +994501112233)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />

              <div className="sq-master-coord-row">
                <IoLocation size={16} />
                <span className="sq-master-coord-text">
                  {picked
                    ? `${Number(picked.latitude).toFixed(5)}, ${Number(
                        picked.longitude
                      ).toFixed(5)} (xəritədə sağ klik edin)`
                    : "Xəritədə sağ klik edərək yeri seçin"}
                </span>
              </div>

              <button
                type="button"
                className={`sq-master-save-btn ${!canSave ? "sq-master-save-disabled" : ""}`}
                onClick={addPlace}
                disabled={!canSave}
              >
                <IoAddCircle size={18} />
                <span>Əlavə et</span>
              </button>
            </div>
          ) : (
            <div className="sq-master-list">
              {items.map((item) => (
                <div
                  key={item.id}
                  ref={(el) => {
                    listRefs.current[item.id] = el;
                  }}
                  className={`sq-master-card ${
                    selectedId === item.id ? "sq-master-card-selected" : ""
                  }`}
                  onClick={() => handleSelectItem(item)}
                >
                  {selectedId === item.id && <div className="sq-master-selected-accent" />}

                  <div className="sq-master-card-main">
                    <div className="sq-master-card-title">{item.name}</div>
                    <div className="sq-master-card-text">İş saatı: {item.hours}</div>
                    <div className="sq-master-card-text">Əlaqə: {item.phone}</div>
                    <div className="sq-master-card-coords">
                      {item.location.latitude.toFixed(5)}, {item.location.longitude.toFixed(5)}
                    </div>
                  </div>

                  <div className="sq-master-card-actions">
                    <button
                      type="button"
                      className="sq-master-call-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        callNow(item.phone);
                      }}
                    >
                      <IoCall size={16} />
                      <span>Zəng et</span>
                    </button>

                    <button
                      type="button"
                      className="sq-master-nav-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDirections(item.location);
                      }}
                    >
                      <IoNavigate size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal.open && (
        <div className="sq-modal-overlay">
          <div className="sq-alert-modal">
            <h3 className={`sq-alert-title sq-alert-${modal.type}`}>
              {modal.title}
            </h3>
            <p className="sq-alert-message">{modal.message}</p>
            <button
              type="button"
              className="sq-alert-button"
              onClick={() =>
                setModal({
                  open: false,
                  title: "",
                  message: "",
                  type: "info",
                })
              }
            >
              Bağla
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterScreen;